import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Truck, Handshake, PenTool, ArrowRight, BadgeDollarSign } from "lucide-react";
import { api, fmt } from "../lib/api";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";
import { useLang } from "../i18n/LanguageContext";
import RichText from "../components/RichText";
import DeliveryZoneMap from "../components/DeliveryZoneMap";

// Page SEO dédiée : « Livraison en main propre de trophées — jusqu'à 50 km ».
// Capte les recherches locales de livraison et convertit vers les pages villes.
// Tous les textes sont éditables dans l'admin (section « Livraison main propre »).
const STEP_ICONS = [PenTool, Handshake, Truck];

export default function LivraisonMainPropre() {
  const { t, ts } = useLang();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings/shop").then((r) => setSettings(r.data)).catch(() => setSettings({}));
  }, []);

  const radius = Math.round(settings?.hand_delivery_radius_km ?? 50);
  const tiers = settings?.hand_delivery_tiers || [
    { max_km: 10, fee: 10 }, { max_km: 20, fee: 15 }, { max_km: 30, fee: 20 }, { max_km: 50, fee: 25 }];

  // Icônes fixes + textes éditables. Le {radius} des étapes est interpolé à la main :
  // t() n'interpole les variables que dans les chaînes, pas dans les tableaux.
  const rawSteps = t("livraison.steps");
  const steps = (Array.isArray(rawSteps) ? rawSteps : []).map((s, i) => ({
    Icon: STEP_ICONS[i] || PenTool,
    h: String(s?.h || "").replaceAll("{radius}", radius),
    p: String(s?.p || ""),
  }));

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: ts("livraison.service_type"),
    provider: { "@type": "LocalBusiness", name: "Mr Trophée", url: "https://mrtrophee.ca" },
    areaServed: { "@type": "GeoCircle", geoMidpoint: { "@type": "GeoCoordinates", latitude: 45.3407383, longitude: -73.8113982 }, geoRadius: `${radius * 1000}` },
    offers: { "@type": "Offer", price: tiers[0].fee, priceCurrency: "CAD" },
  };

  return (
    <div className="pt-32 pb-28 max-w-5xl mx-auto px-6 lg:px-10" data-testid="livraison-page">
      <SEO title={`${ts("livraison.title")} | Mr Trophée`}
        description={ts("livraison.meta", { radius, fee: fmt(tiers[0].fee) })}
        path="/livraison-main-propre" jsonLd={serviceLd} keywords={ts("livraison.seo_keywords")} />
      <FadeUp>
        <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4 flex items-center gap-2">
          <Truck className="w-4 h-4" /> {t("livraison.kicker")}
        </p>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-5" data-testid="livraison-title">{t("livraison.title")}</h1>
        <p className="text-ivory/70 text-base leading-relaxed max-w-3xl mb-3" data-testid="livraison-intro">
          {t("livraison.intro", { radius })}
        </p>
        <p className="text-ivory/55 text-sm leading-relaxed max-w-3xl mb-10">
          {t("livraison.beyond")}
        </p>
      </FadeUp>

      <div className="grid sm:grid-cols-3 gap-4 mb-12" data-testid="livraison-steps">
        {steps.map((s, i) => (
          <FadeUp key={i} delay={i * 0.07}>
            <div className="bg-surface border border-ivory/10 rounded-xl p-6 h-full hover:border-gold/40 transition-colors duration-300" data-testid={`livraison-step-${i}`}>
              <s.Icon className="w-5 h-5 text-gold mb-3" />
              <p className="font-semibold text-sm text-ivory/90 mb-1.5"><RichText text={s.h} path={`livraison.steps.${i}.h`} /></p>
              <p className="text-xs text-ivory/60 leading-relaxed"><RichText text={s.p} path={`livraison.steps.${i}.p`} /></p>
            </div>
          </FadeUp>
        ))}
      </div>

      <FadeUp delay={0.09}>
        <div className="mb-12">
          <DeliveryZoneMap />
        </div>
      </FadeUp>

      <FadeUp delay={0.1}>
        <div className="bg-surface border border-gold/25 rounded-xl p-7 mb-12" data-testid="livraison-zone">
          <p className="text-[11px] uppercase tracking-widest text-gold mb-2 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" /> {t("livraison.zone_title")}
          </p>
          <p className="text-sm text-ivory/70 leading-relaxed mb-5">
            {t("livraison.zone_text", { radius })}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/villes" data-testid="livraison-cta-villes"
              className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full hover:bg-champagne transition-colors duration-300 text-sm">
              <MapPin className="w-4 h-4" /> {t("livraison.cta_villes")}
            </Link>
            <Link to="/contact" data-testid="livraison-cta-contact"
              className="inline-flex items-center gap-2 border border-gold/40 text-gold px-6 py-3 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300 text-sm">
              {t("livraison.cta_contact")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <div className="border border-ivory/10 rounded-xl p-5" data-testid="livraison-price-note">
          <p className="text-[10px] italic text-ivory/45 mb-3 flex items-center gap-1.5">
            <BadgeDollarSign className="w-3 h-3" /> {t("livraison.fee_note")}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4" data-testid="livraison-fee-tiers">
            {tiers.map((tr, i) => (
              <div key={tr.max_km} data-testid={`livraison-tier-${tr.max_km}`}
                className="bg-ink border border-gold/25 rounded-lg px-3 py-2.5 text-center">
                <p className="text-[11px] text-ivory/55 mb-0.5">
                  {i === 0
                    ? t("livraison.tier_under", { km: tr.max_km })
                    : t("livraison.tier_range", { from: tiers[i - 1].max_km, to: tr.max_km })}
                </p>
                <p className="text-gold font-bold text-base">{fmt(tr.fee)}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-ivory/60 leading-relaxed">
            {t("livraison.price_note")}
          </p>
        </div>
      </FadeUp>
    </div>
  );
}
